import AddBoxIcon from "@mui/icons-material/AddBox";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import SettingsIcon from "@mui/icons-material/Settings";
import {
  ClickAwayListener,
  Grow,
  Link,
  MenuItem,
  Paper,
  Popper,
} from "@mui/material";
import { isAxiosError } from "axios";
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { flushSync } from "react-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { USER_STATUS } from "../../../assets/data/common";
import NoRowOverlay from "../../../assets/images/icons/NoRowOverlay";
import { exportUserCSV, getDetailByUser, getUserList } from "../../../axios/User/UserApi";
import {
  ControlpanelPrimaryButton,
  ControlpanelSuccess2thButton,
  ControlpanelSuccess3thButton,
} from "../../../components/StyledButton";
import {
  CustomAdvancedMenuList,
  StyledDataGrid,
} from "../../../components/StyledComponents";
import { useSettings } from "../../../context/SettingsContext";
import { generateTranslateMuiTable } from "../../../utils/Function/commonFunction";
import CustomPagination from "../CustomPagination";
import AddNewEmployeeDialogForm from "./AddNewEmployeeDialogForm";
import CreateUserFromDeletedUsersDialog from "./CreateUserFromDeletedUsersDialog";
import DeleteMultipleUsersDialog from "./DeleteMultipleUsersDialog";
import SelectDataToImportUsersDialog from "./SelectDataToImportUsersDialog";
import Cookies from "js-cookie";
import { useAuth } from "../../../context/AuthContext";
import InviteEmployeesDialogNew from "./InviteEmployeesDialogNew";
import AddIcon from '@mui/icons-material/Add';
import { useLoading } from "../../../context/LoadingContext";
import ServerSearchInput from "../../../components/ServerSearchInput";
import CustomColumnsPanel from "../../../components/CustomColumnsPanel";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { getDesignationOptions } from "../../../assets/data/designationOptions";
import CustomSplitButton from "../../../components/CustomSplitButton";
import ExportIcon16pxSvg from "../../../assets/images/icons/ExportIcon16pxSvg";
import SendV2Icon16pxSvg from "../../../assets/images/icons/SendV2Icon16pxSvg";
import RestoreV2Icon16pxSvg from "../../../assets/images/icons/RestoreV2Icon16pxSvg";
import StarIconSvg from "../../../assets/images/icons/StarIconSvg";
dayjs.extend(utc);
dayjs.extend(timezone)

const MODE = {
  none: -1,
  import: 0,
  export: 1,
  delete: 2,
  invite: 3,
  create: 4,
  createFromDelete: 5,
  advanced: 6,
};

const UserGridInfoTable = ({
  countries,
  userRoles,
  refreshData,
  selectedRow,
  onRowClick,
  onRowDoubleClick,
  onDetailLoadingChange,
  handleRefreshTable,
  department,
  groupData,
  designation
}) => {
  const { genaralSettingDateFormat, dateTimeFormat } =
    useAuth();
  const { t, i18n } = useTranslation();
  const { settings } = useSettings();
  const [mode, setMode] = useState(MODE.none);
  const [emailInvite, setEmailInvite] = useState("");
  const anchorRefAdvanced = useRef(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [totalpages, setTotalPages] = useState(0);

  const tableTranslated = useMemo(() => generateTranslateMuiTable(t), [t]);
  const { profile, isEmployee, isAdmin } = useAuth();
  const [rowCount, setRowCount] = useState(0);
  const [rowData, setRowData] = useState([]);
  const [, setFilteredRowData] = useState([]);
  const [filteredRowCount, setFilteredRowCount] = useState(0);
  const [show, hide] = useLoading();
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const searchTimeoutRef = useRef(null);
  const [isSm, setIsSm] = useState(false)
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    const handleResize = () => {
      setIsSm(window.innerWidth < 540);
    };
    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleSearchChange = (value) => {
    setSearchText(value);
  };

  const encodedSearchText = useMemo(() => {
    return searchText;
  }, [searchText]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchText(encodedSearchText);
      setPage(0);
    }, 700);

    return () => clearTimeout(handler);
  }, [encodedSearchText]);

  const convertFormat = (format) => {
    return format
      .replace(/dd/g, "DD")
      .replace(/MM/g, "MM")
      .replace(/yyyy/g, "YYYY");
  };

  const designationOptions = getDesignationOptions(t);

  const designationMap = useMemo(() => {
    const map = new Map();
    designation.forEach((d) => map.set(Number(d.id), d.designationName));
    return map;
  }, [designation]);

  const getUserStatusDisplay = useCallback(
    (status) => {
      if (status === USER_STATUS.unblock) {
        return {
          label: t("common.active"),
          color: "#058900",
        };
      }

      if (status === USER_STATUS.block) {
        return {
          label: t("common.blocked"),
          color: "#D00D00",
        };
      }

      if (status === USER_STATUS.pendingActivation) {
        return {
          label: t("common.pending-activation"),
          color: "#F97C00",
        };
      }

      return {
        label: t("common.content-150"),
        color: "#9CA3AF",
      };
    },
    [t]
  );

  const columnData = useMemo(
    () => [
      {
        field: "employeeCode",
        headerName: t("common.employee-code"),
        minWidth: 150,
        flex: 1,
        hideable: true,
        rowSpanValueGetter: (value, row) =>
          row ? `${row.id}-${row.employeeCode}` : value,
        valueFormatter: (value) => {
          return value === null || value === undefined || value === ""
            ? "-"
            : `${value}`;
        },
      },
      {
        field: "fullName",
        headerName: t("common.fullname"),
        minWidth: 200,
        hideable: true,
      },
      {
        field: "telephone",
        headerName: t("common.phone"),
        minWidth: 150,
        flex: 1,
        hideable: true,
        valueFormatter: (value) => {
          return value === null || value === undefined || value === ""
            ? "-"
            : `${value}`;
        },
      },
      {
        field: "designationId",
        headerName: t("common.designation"),
        minWidth: 150,
        flex: 1,
        hideable: true,
        valueGetter: (value) => designationMap.get(Number(value)) || "-",
      },
      {
        field: "administratorRights",
        headerName: t("common.admin"),
        minWidth: 180,
        hideable: true,
        rowSpanValueGetter: (value, row) =>
          row ? `${row.id}-${row.administratorRights}` : value,
        renderCell: (params) => {
          const { administratorRights: rights, owner } = params.row || {};

          // mapping key dịch
          const roleLabelMap = {
            EMPLOYEE: t("roles2.Employee"),
            AUDITOR: t("roles2.Auditor"),
            ADMIN: t("roles2.Admin"),
            SUBCRIPTION: t("roles2.SubscriptionAdmin"),
          };

          const displayValue =
            roleLabelMap[rights] ||
            (rights !== null && rights !== undefined && rights !== ""
              ? rights
              : "-");

          return (
            <p className="flex items-center">
              <span className="mr-1">{displayValue}</span>
              {owner && (
                <StarIconSvg/>
              )}
            </p>
          );
        },
      },
      {
        field: "active",
        headerName: t("common.employee-status"),
        minWidth: 150,
        flex: 1,
        hideable: true,
        rowSpanValueGetter: (value, row) =>
          row ? `${row.id}-${row.active}` : value,
        valueGetter: (value) => {
          return getUserStatusDisplay(value).label;
        },
        renderCell: (params) => {
          const statusDisplay = getUserStatusDisplay(params.row?.active);

          return (
            <span style={{ color: statusDisplay.color }}>
              {statusDisplay.label}
            </span>
          );
        },
      },
      {
        field: "userTypeId",
        headerName: t("common.user-type"),
        minWidth: 200,
        flex: 1,
        hideable: true,
        rowSpanValueGetter: (value, row) =>
          row && row.userTypeId != null
            ? `${row.id}-${row.userTypeId}`
            : value,
        valueFormatter: (value) => {
          if (value === null || value === undefined) return "-";
          if (value === 1) return t("common.full-time");
          if (value === 2) return t("common.part-time");
        },
      },
      {
        field: "joinDate",
        headerName: t("common.joinedDate"),
        minWidth: 200,
        flex: 1,
        hideable: true,
        valueFormatter: (value) => {
          if (!value) return "-";

          const format = convertFormat(genaralSettingDateFormat);

          return dayjs
            .utc(value, "YYYY-MM-DD")
            .local()
            .format(format);
        }
      },
      {
        field: "createdTime",
        headerName: t("common.created-time"),
        minWidth: 200,
        flex: 1,
        hideable: true,
        valueFormatter: (value) => {
          if (!value) return "-";

          const format = convertFormat(dateTimeFormat);

          return dayjs
            .utc(value, "YYYY-MM-DD HH:mm:ss")
            .local()
            .format(format);
        }
      },
      {
        field: "modifiedTime",
        headerName: t("common.modified-time"),
        minWidth: 200,
        flex: 1,
        hideable: true,
        valueFormatter: (value) => {
          if (!value) return "-";

          const format = convertFormat(dateTimeFormat);

          return dayjs
            .utc(value, "YYYY-MM-DD HH:mm:ss")
            .local()
            .format(format);
        }
      },
    ],
    [t, settings, dateTimeFormat, designationMap, getUserStatusDisplay]
  );

  useEffect(() => {
    const fetchData = async () => {
      show();
      onDetailLoadingChange?.(true);
      try {
        // Employee → chỉ lấy detail 1 user
        if (isEmployee) {
          if (!profile?.userId) return;

          const response = await getDetailByUser({ id: profile.userId });
          const raw = response?.data?.data;
          const user = Array.isArray(raw) ? raw[0] : raw;

          if (user) {
            setRowData([user]);
            setFilteredRowData([user]);
            setRowCount(1);
            setFilteredRowCount(1);
            onRowClick(user);
          }

          return;
        }

        // Admin → list users, rồi lấy detail user đang chọn / user đầu tiên
        const payload = {
          statusId: 0,
          page: page + 1,
          pageSize: pageSize,
          status: true,
          langId: localStorage.getItem("i18nextLngId"),
          searchText: debouncedSearchText || null,
        };

        const response = await getUserList(payload);

        const employees = response.data.data || [];

        setRowCount(response.data.pagination.totalElements);
        setRowData(employees);
        setFilteredRowData(employees);
        setFilteredRowCount(response.data.pagination.totalElements);
        setTotalPages(response.data.pagination.totalPages);

        if (employees.length > 0) {
          const selectedUserId = selectedRow?.userId;
          const matchedUser = selectedUserId
            ? employees.find((employee) => employee.userId === selectedUserId)
            : null;
          const targetUser = matchedUser || employees[0];

          if (targetUser?.userId) {
            const detailRes = await getDetailByUser({ id: targetUser.userId });
            const raw = detailRes?.data?.data;
            const detailUser = Array.isArray(raw) ? raw[0] : raw;
            onRowClick(detailUser || targetUser);
          }
        }
      } catch (error) {
        if (error.response && error.response.status !== 401) {
          toast.error(t(error?.response.data));
        } else {
          toast.error(t("common.network-timeout"));
        }
      } finally {
        hide();
        onDetailLoadingChange?.(false);
      }
    };

    fetchData();
  }, [
    page,
    pageSize,
    debouncedSearchText,
    refreshData,
    profile?.userId,
    isEmployee,
  ]);

  function getCurrentTimestamp() {
    const now = new Date();
    const yyyy = now.getFullYear();
    const MM = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const HH = String(now.getHours()).padStart(2, '0');
    const mm = String(now.getMinutes()).padStart(2, '0');
    const ss = String(now.getSeconds()).padStart(2, '0');

    return `${yyyy}${MM}${dd}${HH}${mm}${ss}`;
  }

  const exportCSV = async () => {
    const payload = {
      statusId: 0,
      page: totalpages,
      pageSize: pageSize,
      status: true,
      langId: localStorage.getItem("i18nextLngId"),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      formatDate: dateTimeFormat,
    };
    try {
      show();
      const response = await exportUserCSV(payload);

      if (response) {
        const blob = new Blob([response.data], { type: 'application/octet-stream' });
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        // a.download = 'work-hour-summary-report.csv';
        const filename = `${t("export.export-user")}_${getCurrentTimestamp()}.xlsx`;

        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
    } catch (error) {
      if (error.response) {
        if (error.response.status !== 401) {
          toast.error(t(error?.response.data));
        }
      } else {
        toast.error(t('common.network-timeout'));
      }
      console.error('Error fetching user list:', error);
    } finally {
      hide();
    }
  };

  useEffect(() => {
    // flushSync: mount dialog ngay trong cùng tick → tour không phải poll lâu
    window.__openAddUserDialog = () => {
      flushSync(() => {
        setMode(MODE.create);
      });
    };
    window.__closeAddUserDialog = () => {
      flushSync(() => {
        setMode(MODE.none);
      });
    };
    window.__openAdvancedMenu = () => {
      flushSync(() => setMode(MODE.advanced));
    };
    window.__closeAdvancedMenu = () => {
      flushSync(() => setMode(MODE.none));
    };
    window.__openInviteDialog = () => {
      flushSync(() => setMode(MODE.invite));
    };
    window.__closeInviteDialog = () => {
      flushSync(() => setMode(MODE.none));
    };
    window.__openRestoreDialog = () => {
      flushSync(() => setMode(MODE.createFromDelete));
    };
    window.__closeRestoreDialog = () => {
      flushSync(() => setMode(MODE.none));
    };

    return () => {
      delete window.__openAddUserDialog;
      delete window.__closeAddUserDialog;
      delete window.__openAdvancedMenu;
      delete window.__closeAdvancedMenu;
      delete window.__openInviteDialog;
      delete window.__closeInviteDialog;
      delete window.__openRestoreDialog;
      delete window.__closeRestoreDialog;
    };
  }, []);

  const templateToolbar = useCallback(
    () => (
      <div className="flex gap-2 justify-between mb-3">
        <div className="flex gap-3">
          <div className="flex items-center justify-end gap-3" data-tour="user-management-add-user-button">
            <CustomSplitButton
              label={t("button.add")}
              startIcon={<AddIcon />}
              onMainClick={() => setMode(MODE.create)}
              onDropdownClick={() => setMode(MODE.advanced)}
              ref={anchorRefAdvanced}
            />
          </div>
        </div>

        <ServerSearchInput onChange={handleSearchChange} />
      </div>
    ),
    [t]
  );

  const handleRowClick = async (params) => {
    const rowData = params.row;
    if (!rowData?.userId) return;

    // Click → luôn lấy detail từ API, không dùng raw row của list
    show();
    onDetailLoadingChange?.(true);
    try {
      const response = await getDetailByUser({ id: rowData.userId });
      const raw = response?.data?.data;
      const detailUser = Array.isArray(raw) ? raw[0] : raw;
      onRowClick(detailUser || rowData);
    } catch (error) {
      if (error.response && error.response.status !== 401) {
        toast.error(t(error?.response?.data?.message || error?.response?.data));
      } else {
        toast.error(t("common.network-timeout"));
      }
      onRowClick(rowData);
    } finally {
      hide();
      onDetailLoadingChange?.(false);
    }
  };

  const handleRowDoubleClick = async (params) => {
    // ❌ Employee không được edit
    if (!isAdmin) return;

    const rowData = params.row;
    if (!rowData?.userId) return;

    show();
    onDetailLoadingChange?.(true);
    try {
      const response = await getDetailByUser({ id: rowData.userId });
      const raw = response?.data?.data;
      const detailUser = Array.isArray(raw) ? raw[0] : raw;
      onRowDoubleClick(detailUser || rowData);
    } catch (error) {
      if (error.response && error.response.status !== 401) {
        toast.error(t(error?.response?.data?.message || error?.response?.data));
      } else {
        toast.error(t("common.network-timeout"));
      }
      onRowDoubleClick(rowData);
    } finally {
      hide();
      onDetailLoadingChange?.(false);
    }
  };

  const handleCloseDialog = () => {
    setMode(MODE.none);
    setEmailInvite("");
  };

  const [columnVisibilityModel, setColumnVisibilityModel] = useState({
    employeeCode: false,
    fullName: true,
    firstName: true,
    lastName: true,
    telephone: false,
    designation: false,
    createdTime: true,
    modifiedTime: false,
    administratorRights: true,
    active: true,
    userTypeId: true,
  });
  const emailUser = profile?.email;
  const handleColumnVisibilityChange = (newModel) => {
    setColumnVisibilityModel(newModel);
    Cookies.set(`UserGridInfoTable_${emailUser}`, JSON.stringify(newModel), {
      expires: 90,
      path: "/",
    });
  };

  useEffect(() => {
    const saved = Cookies.get(`UserGridInfoTable_${emailUser}`);
    if (saved) {
      setColumnVisibilityModel(JSON.parse(saved));
    }
  }, []);

  const searchableFields = ["fullName", "employeeCode", "email", "active"];

  const removeVietnameseTones = (str) => {
    return str
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();
  };

  const handleRowsPerPageChange = (newPageSize) => {
    // newPageSize ở đây tuỳ CustomPagination, có thể là event hoặc number
    const size =
      typeof newPageSize === "number"
        ? newPageSize
        : parseInt(newPageSize.target.value, 10);

    setPage((prevPage) => {
      // index của record đầu tiên đang xem
      const firstRowIndex = prevPage * pageSize;

      // page mới sao cho vẫn chứa record đó
      const newPage = Math.floor(firstRowIndex / size);

      // phòng trường hợp vượt quá số trang
      const maxPage = Math.max(
        0,
        Math.ceil(filteredRowCount / size) - 1
      );

      return Math.min(newPage, maxPage);
    });

    setPageSize(size);
  };

  return (
    <Fragment>
      <StyledDataGrid
        data-tour="user-management-user-table"
        showColumnVerticalBorder
        sx={{
          height: "80.5vh !important",
          ".MuiDataGrid-main": {
            height: "75vh",
          },

          "& .MuiDataGrid-scrollbar--vertical, & .MuiDataGrid-scrollbar--horizontal":
          {
            display: "none !important",
          },

          "& .MuiDataGrid-scrollbarFiller": {
            display: "none !important",
          },
          "& .MuiDataGrid-columnHeaders": {
            paddingRight: 0,
          },
          "& .MuiDataGrid-topContainer": {
            paddingRight: 0,
          },

          "&": {
            "--DataGrid-scrollbarSize": "0px",
          },

          "& .MuiDataGrid-virtualScroller": {
            scrollbarWidth: "none",
          },
          "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
            width: 0,
            height: 0,
          },
          "&:hover .MuiDataGrid-virtualScroller, &:focus-within .MuiDataGrid-virtualScroller":
          {
            scrollbarWidth: "thin",
          },
          "&:hover .MuiDataGrid-virtualScroller::-webkit-scrollbar, &:focus-within .MuiDataGrid-virtualScroller::-webkit-scrollbar":
          {
            width: 10,
            height: 10,
          },

          '& [class*="MuiDataGrid-scrollbar"][class*="--horizontal"]': {
            display: "none !important",
          },

          "& input[type=number]::-webkit-inner-spin-button, \
 & input[type=number]::-webkit-outer-spin-button": {
            WebkitAppearance: "none",
            margin: 0,
          },

          "& input[type=number]": {
            textAlign: "center",
            MozAppearance: "textfield",
            WebkitAppearance: "none",
            padding: "5px !important",
            height: "100%",
            pointerEvents: "none",
            caretColor: "transparent",
            userSelect: "none",
            paddingTop: "8px !important",
            paddingBottom: "8px !important"
          },
        }}
        rows={rowData || []}
        rowCount={rowCount}
        paginationMode="client"
        columns={columnData}
        // loading={gridLoading}
        getRowId={(r) => r.userId}
        slots={{
          ...(isAdmin && { toolbar: templateToolbar }),
          pagination: CustomPagination,
          noRowsOverlay: NoRowOverlay,
          columnsPanel: CustomColumnsPanel,
        }}
        slotProps={{
          pagination: {
            page,
            count: rowCount,
            rowsPerPage: pageSize,
            onPageChange: setPage,
            onRowsPerPageChange: handleRowsPerPageChange,
          },
        }}
        localeText={tableTranslated}
        onRowDoubleClick={handleRowDoubleClick}
        onRowClick={handleRowClick}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={handleColumnVisibilityChange}
      />

      {mode === MODE.import && (
        <SelectDataToImportUsersDialog onClose={handleCloseDialog} />
      )}
      {/* {mode === MODE.export && (
        <SelectDataToExportUsersDialog onClose={handleCloseDialog} />
      )} */}
      {mode === MODE.createFromDelete && (
        <CreateUserFromDeletedUsersDialog
          handleRefreshTable={handleRefreshTable}
          onClose={handleCloseDialog}
        />
      )}
      {mode === MODE.delete && (
        <DeleteMultipleUsersDialog onClose={handleCloseDialog} />
      )}
      {mode === MODE.invite && (
        // <InviteEmployeesDialog
        //   defaultValue={emailInvite}
        //   onClose={handleCloseDialog}
        // />
        <InviteEmployeesDialogNew
          defaultValue={emailInvite}
          onClose={handleCloseDialog}
        />
      )}
      {mode === MODE.create && (
        <>
          <AddNewEmployeeDialogForm
            userRoles={userRoles}
            countries={countries}
            handleOpenInvite={(email) => {
              setMode(MODE.invite);
              setEmailInvite(email);
            }}
            handleRefreshTable={handleRefreshTable}
            onClose={handleCloseDialog}
            department={department}
            groupData={groupData}
            designation={designation}
          />

        </>

      )}

      {mode === MODE.advanced && (
        <Popper
          open
          anchorEl={anchorRefAdvanced.current}
          placement="bottom-start"
          transition
          disablePortal
          className="z-10"
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              timeout={window.__tourIsOpen ? 0 : undefined}
              style={{
                transformOrigin:
                  placement === "bottom" ? "left top" : "left bottom",
              }}
            >
              <Paper style={{ borderRadius: "8px", }}>
                <ClickAwayListener
                  onClickAway={(e) => {
                    if (window.__tourIsOpen) return;
                    handleCloseDialog(e);
                  }}
                >
                  <CustomAdvancedMenuList
                    id="advanced-menu"
                    aria-labelledby="advanced-button"
                  >
                    <MenuItem
                      data-tour="user-management-export-button"
                      style={{
                        borderBottom: "1px solid #E5E7EB",
                        pointerEvents: "auto",
                      }}
                      onClick={() => exportCSV()}
                    >
                      <Link>
                        <ExportIcon16pxSvg />
                        <p>{t("button.export-data")}</p>
                      </Link>
                    </MenuItem>
                    <MenuItem
                      data-tour="user-management-restore-button"
                      onClick={() => setMode(MODE.createFromDelete)}
                      style={{ pointerEvents: "auto" }}
                    >
                      <Link>
                        <RestoreV2Icon16pxSvg />
                        <p>{t("button.create-user-from-deleted-users")}</p>
                      </Link>
                    </MenuItem>
                    <MenuItem
                      data-tour="user-management-invite-button"
                      onClick={() => setMode(MODE.invite)}
                    >
                      <Link>
                        <SendV2Icon16pxSvg />
                        <p>{t("button.invite-employees")}</p>
                      </Link>
                    </MenuItem>
                  </CustomAdvancedMenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      )}
    </Fragment>
  );
};

export default UserGridInfoTable;
